import { useEffect, useState } from 'react';
import { Alert, FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { IManagementUnitAddEditRequest } from '../../../../model/apm/management-unit/request/management-unit-add-edit-request';
import { IManagementUnitResponse } from '../../../../model/apm/management-unit/response/management-unit-response';

interface ManagementUnitDrawerProps {
    open: boolean;
    initial?: IManagementUnitResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IManagementUnitAddEditRequest) => void;
}

const emptyValue: IManagementUnitAddEditRequest = { isActive: true };

export function ManagementUnitDrawer({ open, initial, isSaving, onClose, onSave }: ManagementUnitDrawerProps) {
    const [form, setForm] = useState<IManagementUnitAddEditRequest>(emptyValue);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const nameError = showValidationErrors && !form.name?.trim();

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? { name: initial.name, operation: initial.operation, otherInformation: initial.otherInformation, isActive: initial.isActive }
            : { ...emptyValue });
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IManagementUnitAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.name?.trim()) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            name: form.name.trim(),
            operation: form.operation || undefined,
            otherInformation: form.otherInformation || undefined,
        });
    }

    return (
        <FormDrawer
            open={open}
            icon={<ApartmentIcon />}
            title={isEdit ? 'Editar unidade gestora' : 'Nova unidade gestora'}
            subtitle={isEdit
                ? `${initial?.name || ''} · ${initial?.biddingCount ?? 0} licitação(ões) vinculada(s)`
                : 'Unidade responsável pelas licitações publicadas no portal'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar unidade'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Nome</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.name || ''}
                            onChange={(e) => update({ name: e.target.value })}
                            error={nameError}
                        />
                        {nameError
                            ? <FormHelperText error>Nome é obrigatório</FormHelperText>
                            : <FormHelperText>Não pode repetir o nome de outra unidade gestora da entidade</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Informações ao cidadão" description="Exibidas no portal junto às licitações da unidade (opcionais).">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>Funcionamento</InputLabel>
                        <RichTextEditor value={form.operation} onChange={(operation) => update({ operation })} minHeight={120} />
                        <FormHelperText>Ex.: horário de atendimento, endereço e contatos</FormHelperText>
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Outras informações</InputLabel>
                        <RichTextEditor value={form.otherInformation} onChange={(otherInformation) => update({ otherInformation })} minHeight={120} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Situação">
                <SwitchField
                    label="Unidade ativa"
                    description="Unidades inativas não aparecem para seleção em novas licitações."
                    checked={form.isActive}
                    onChange={(isActive) => update({ isActive })}
                />
                {isEdit && !form.isActive && (initial?.biddingCount ?? 0) > 0 && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>As licitações já vinculadas continuam com esta unidade.</Alert>
                )}
            </FormDrawerGroup>
        </FormDrawer>
    );
}
